import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useContext, useRef } from 'react';
import { mergeTicketTypeUpdates } from '../utils/mergeTicketTypeUpdates';

import { ToastContainer, toast } from 'react-toastify';
import {
  Announcement01,
  Badge,
  Button,
  Card,
  ChevronRight,
  Image01,
  List,
  Modal,
  Tabs,
  Ticket01,
  Tool01,
  UploadCloud01,
  Zap,
} from '@opendate-app/setlist';
const axios = require('axios').default;

import { ActionCableContext } from '../startup/ConfirmTicketingApp';

import {
  DndContext, 
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';

import TicketTypeRow from './TicketTypeRow';
import BasicInfo from './BasicInfo';
import Details from './Details';
import Tickets from './Tickets';
import Publish from './Publish';
import Settings from './Settings';
import EventDetailsApp from '../../EventDetails/startup/EventDetailsApp';
import Automations from './Automations';
import Promotions from './Promotions';
import Customize from './Customize';

import {
  offsetDateForBrowser
} from '../../../shared/timeZoneLogic';
import { toastError } from '../../../shared/toastHelper';
import { fetchReloadedRootTicketGroup, ticketDragMove } from './ticketTypeFormHelpers';
import { calendarClassificationBadge } from '../../../shared/calendarClassificationBadge';

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

import {
  BASIC_INFO_MENU_ITEM,
  DETAILS_MENU_ITEM,
  TICKETS_MENU_ITEM,
  AUTOMATION_MENU_ITEM,
  PROMOTION_MENU_ITEM,
  PUBLISH_MENU_ITEM,
  SETTINGS_MENU_ITEM,
  CUSTOMIZE_MENU_ITEM,
} from '../constants/confirmTicketingConstants';

const beforeUnloadListener = (e) => {
  e.preventDefault();
  // Most modern browsers ignore custom messages for the beforeunload dialog now,
  // but we'll still return a value here to make sure the dialog is shown.
  e.returnValue = 'Tickets will not go on sale until the event is published';
  return e.returnValue;
};

const getAllTicketTypes = (ticketGroup) => {
  if (!ticketGroup) return [];

  let allTickets = [...(ticketGroup.ticket_types || [])];

  if (ticketGroup.sub_groups && ticketGroup.sub_groups.length > 0) {
    ticketGroup.sub_groups.forEach(subGroup => {
      allTickets = [...allTickets, ...getAllTicketTypes(subGroup)];
    });
  }

  return allTickets;
};

const ConfirmTicketing = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  confirmChanged,
  activeMenuItem,
  activeMenuItemChanged,
  updateConfirm,
  autoSaveConfirm,
  venuesOptions,
  rooms,
  venueOwnerships,
  performers,
  deletePerformer,
  createPerformer,
  updatePerformer,
  pageDetails,
  createPageDetail,
  deletePageDetail,
  pageDetailChanged,
  autoSavePageDetail,
  pageDetailDragged,
  imageCalendarEventAttachments,
  uploadPageDetailImage,
  uploadPageDetailImageUrl,
  uploadConfirmMainImageFile,
  uploadConfirmMainImageUrl,
  uploadConfirmWebsiteImageFile,
  uploadConfirmWebsiteImageUrl,
  activeTicketingMenuItem,
  activeTicketingMenuItemChanged,
  ticketTypeForForm,
  ticketTypeForFormChanged,
  fetchNewTicketType,
  fetchTicketTypeForEdit,
  mainEventImageLoading,
  websiteEventImageLoading,
  publishConfirm,
  rootUrl,
  addOns,
  addOnForForm,
  fetchNewAddOn,
  addOnForFormChanged,
  createAddOn,
  fetchAddOnToEdit,
  deleteAddOn,
  updateAddOn,
  addOnDragged,
  statementDescriptorSuffix,
  statementDescriptorSuffixChanged,
  maxDescriptorLength,
  updateStatementDescriptorSuffix,
  artistBios,
  artistBiosChanged,
  pageDetailTemplates,
  deletePageDetailTemplate,
  pageDetailTemplate,
  pageDetailTemplateChanged,
  createPageDetailTemplate,
  confirmationPageTemplates,
  performerDragged,
  exampleTicket,
  buildExampleTicket,
  exampleTicketChanged,
  seatingCharts,
  seatsIOPublicKey,
  isLoadingDefaultTicketTypes,
  addOnSeatingCharts,
  isEditingAddOnsIntro,
  isEditingAddOnsIntroChanged,
  addOnsIntroEditorState,
  addOnsIntroEditorStateChanged,
  isLoadingDefaultAddOns,
  isEditingAddOnsAlertMessage,
  isEditingAddOnsAlertMessageChanged,
  addOnsAlertMessageEditorState,
  addOnsAlertMessageEditorStateChanged,
  promoters,
  buyers,
  requirePerTicketCustomChargeTypes,
  createRequirePerTicketCustomChargeType,
  requirePerTicketCustomChargeTypeChanged,
  deleteRequirePerTicketCustomChargeType,
  autoSaveRequirePerTicketCustomChargeType,
  showActivities,
  manualTaxes,
  warnBeforeLeaving,
  warnBeforeLeavingChanged,
  warnBeforeLeavingOpen,
  warnBeforeLeavingOpenChanged,
  warnBeforeLeavingCallback,
  warnBeforeLeavingCallbackChanged,
  addOnAdded,
  feeRuleSets,
  preSaleEnabled,
  checkoutCategories,
  checkoutCategoriesChanged,
  seatingChartEvents,
  membershipProducts,
  widgetConfig,
  widgetConfigChanged,
  webOrderFormProps,
  widgetPickerAvailable,
  eventOrderFormAvailable,
  customWidgetAvailable,
  checkoutStructureSelfManaged,
  markCheckoutStructureSelfManaged,
  rootTicketGroup,
  ticketGroupChanged,
  isLoadingDefaultTicketTypesChanged,
  addOnsChanged,
  isLoadingDefaultAddOnsChanged,
  admissionSeatingChartEvent,
  admissionSeatingChartEventChanged,
  addOnSeatingChartEvent,
  addOnSeatingChartEventChanged,
  seatingChartEventsChanged,
  kills,
  fetchKills,
  createKill,
  updateKill,
  deleteKill,
  holds,
  fetchHolds,
  createHold,
  updateHold,
  deleteHold,
  generalAdmissionHolds,
  fetchGeneralAdmissionHolds,
  createGeneralAdmissionHold,
  updateGeneralAdmissionHold,
  deleteGeneralAdmissionHold,
  addOnsIntroTemplates,
  addOnsAlertMessageTemplates,
  addOnsIntroTemplateDraft,
  addOnsAlertMessageTemplateDraft,
  addOnMessageTemplateChanged,
  createAddOnMessageTemplate,
  deleteAddOnMessageTemplate
}) => {
  const cable = useContext(ActionCableContext).cable;

  const [googleMarketingPixels, setGoogleMarketingPixels] = useState(
    confirm.google_marketing_pixels.map((pixel) => ({value: pixel}))
  );

  const [facebookMarketingPixels, setFacebookMarketingPixels] = useState(
    confirm.facebook_marketing_pixels.map((pixel) => ({value: pixel}))
  );

  const [tiktokMarketingPixels, setTiktokMarketingPixels] = useState(
    confirm.tiktok_marketing_pixels.map((pixel) => ({value: pixel}))
  );

  const [spotifyMarketingPixels, setSpotifyMarketingPixels] = useState(
    confirm.spotify_marketing_pixels.map((pixel) => ({value: pixel}))
  );

  const [xMarketingPixels, setXMarketingPixels] = useState(confirm.x_marketing_pixels || []);

  // ponytail: the pixel inputs render from the useState above, never from
  // confirm.*_marketing_pixels, so this save deliberately dispatches nothing
  // back into Redux. Dispatching updateConfirm here wrote a whole confirm
  // captured at page load, silently reverting anything edited while the
  // request was in flight (O-5794 blocked Publish that way).
  const initialPixelRender = useRef(true);
  // ponytail: chain saves off the previous one so the server sees edits in
  // order -- two quick edits would otherwise fly as concurrent whole-snapshot
  // PATCHes and an out-of-order server could let the older one win.
  const pixelSaveChain = useRef(Promise.resolve());

  useEffect(() => {
    if (initialPixelRender.current) {
      initialPixelRender.current = false;
      return;
    }

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    const snapshot = {
      google_marketing_pixels: googleMarketingPixels.map(pixel => pixel.value),
      facebook_marketing_pixels: facebookMarketingPixels.map(pixel => pixel.value),
      tiktok_marketing_pixels: tiktokMarketingPixels.map(pixel => pixel.value),
      spotify_marketing_pixels: spotifyMarketingPixels.map(pixel => pixel.value),
      x_marketing_pixels: xMarketingPixels
    };

    pixelSaveChain.current = pixelSaveChain.current
      .then(() => axios.patch(`/teams/${team.id}/confirms/${confirm.id}`, { confirm: snapshot }))
      .catch((error) => {
        toast.error(error.response?.data?.join(", ") || "Could not save marketing pixels. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  }, [googleMarketingPixels, facebookMarketingPixels, tiktokMarketingPixels, spotifyMarketingPixels, xMarketingPixels]);

  useEffect(() => {
    if (cable) {
      const subscription = cable.subscriptions.create(
        {
          channel: "InternalConfirmChannel",
          id: confirm.id
        },
        {
          received: (message) => {
            if (message.ticket_types && Array.isArray(message.ticket_types)) {
              ticketGroupChanged(mergeTicketTypeUpdates(rootTicketGroupRef.current, message.ticket_types));
            }
            if (message.add_ons && Array.isArray(message.add_ons)) {
              const updatesMap = new Map(
                message.add_ons.map(update => [update.id, update])
              );
              const merged = addOnsRef.current.map(ao => {
                const update = updatesMap.get(ao.id);
                return update ? { ...ao, ...update } : ao;
              });
              addOnsChanged(merged);
            }
            if (message.demand_status !== undefined) {
              confirmChanged({
                ...confirmRef.current,
                demand_status: message.demand_status,
                demand_multiplier: message.demand_multiplier
              });
            }
          }
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [cable, confirm.id]);

  const ticketTypeIsSeated = (ticketType) => {
    return (
      ticketType
        && ticketType.seating_chart_category_key
        && ticketType.seating_chart_category_key.length > 0
    )
  }

  useEffect(() => {
    var updated = Object.assign({}, confirm, {
      delivery_time: offsetDateForBrowser(confirm.delivery_time),
    });

    requirePerTicketCustomChargeTypes.forEach((cct) => {
      if(cct.default_amount){
        var updated = Object.assign({}, cct, {
          default_amount: parseFloatFromString(cct.default_amount).toFixed(2)
        });

        requirePerTicketCustomChargeTypeChanged(updated);
      }
    });

    if(requirePerTicketCustomChargeTypes.length === 0){
      createRequirePerTicketCustomChargeType(csrfToken, team, confirm);
    }

    confirmChanged(updated);
  }, []);

  useEffect(() => {
    if(confirm.published_at) {
      window.removeEventListener("beforeunload", beforeUnloadListener);
    }
  }, [confirm]);

  const rootTicketGroupRef = useRef(rootTicketGroup);

  useEffect(() => {
    rootTicketGroupRef.current = rootTicketGroup;
  }, [rootTicketGroup]);

  const ticketTypesChanged = useCallback((updates) => {
    ticketGroupChanged(mergeTicketTypeUpdates(rootTicketGroupRef.current, updates));
  }, [ticketGroupChanged]);

  const confirmRef = useRef(confirm);

  useEffect(() => {
    confirmRef.current = confirm;
  }, [confirm]);

  const addOnsRef = useRef(addOns);

  useEffect(() => {
    addOnsRef.current = addOns;
  }, [addOns]);

  const checkWarnBeforeLeaving = (callback) => {
    if(warnBeforeLeaving) {
      warnBeforeLeavingCallbackChanged(() => {
        callback();
      });

      warnBeforeLeavingOpenChanged(true);
    } else {
      callback();
    }
  };
  
  const reloadTicketTypes = () => {
    isLoadingDefaultTicketTypesChanged(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    fetchReloadedRootTicketGroup(team, confirm, rootTicketGroup)
    .then(ticketGroupChanged)
    .catch(() => {
      toastError("Failed to reload ticket types after sync");
    })
    .finally(() => {
      isLoadingDefaultTicketTypesChanged(false);
    });
  };
  
  const reloadAddOns = () => {
    isLoadingDefaultAddOnsChanged(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/confirms/${confirm.id}/add_ons`)
    .then(({ data }) => {
      addOnsChanged(data);
    })
    .catch(() => {
      toastError("Failed to reload add-ons after sync");
    })
    .finally(() => {
      isLoadingDefaultAddOnsChanged(false);
    });
  };

  const reloadCheckoutCategories = () => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    return axios.get(`/teams/${team.id}/confirms/${confirm.id}/checkout_categories`)
      .then(({ data }) => {
        if (!Array.isArray(data)) return;

        checkoutCategoriesChanged(data);
      })
      .catch(() => {
        toastError("Failed to reload checkout structure");
      });
  };

  const ticketTypes = getAllTicketTypes(rootTicketGroup);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [dragData, setDragData] = useState(null);
  const [refusedDrag, setRefusedDrag] = useState(null);

  const findTicketGroup = (ticketGroupId, group) => {
    if (group.id === ticketGroupId) {
      return group;
    }

    for (const subGroup of (group.sub_groups || [])) {
      const found = findTicketGroup(ticketGroupId, subGroup);
      if (found) {
        return found;
      }
    }

    return null;
  }

  const moveBetweenTwoTicketGroups = (active, over, activeTicketType, activeTicketGroup, overTicketType, overTicketGroup) => {
    const removeTicketTypeFromGroups = (group) => {
      if (!group) return group;

      return {
        ...group,
        ticket_types: group.ticket_types.filter(tt => tt.id !== active.id),
        sub_groups: (group.sub_groups || []).map(subGroup =>
          removeTicketTypeFromGroups(subGroup)
        )
      };
    };
    let updatedRootGroup = removeTicketTypeFromGroups(rootTicketGroup);

    const updateDestinationGroup = (group) => {
      if (group.id === overTicketGroup.id) {
        const newIndex = group.ticket_types.findIndex(tt => tt.id === over.id);
        const updatedTypes = [...group.ticket_types];
        updatedTypes.splice(newIndex, 0, {
          ...activeTicketType,
          position: newIndex + 1
        });

        return {
          ...group,
          ticket_types: updatedTypes.map((tt, index) => ({
            ...tt,
            position: index + 1
          }))
        };
      }

      return {
        ...group,
        sub_groups: (group.sub_groups || []).map(subGroup =>
          updateDestinationGroup(subGroup)
        )
      };
    };
    updatedRootGroup = updateDestinationGroup(updatedRootGroup);

    ticketGroupChanged(updatedRootGroup);
  };

  const moveWithinTicketGroup = (active, over, activeTicketType, activeTicketGroup, overTicketType, overTicketGroup) => {
    const oldIndex = activeTicketGroup.ticket_types.findIndex(tt => tt.id === active.id);
    const newIndex = activeTicketGroup.ticket_types.findIndex(tt => tt.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const updatedTicketTypes = arrayMove(
        activeTicketGroup.ticket_types,
        oldIndex,
        newIndex
      );

      const reorderedTypes = updatedTicketTypes.map((tt, index) => ({
        ...tt,
        position: index + 1
      }));

      const updatedTicketGroup = {
        ...activeTicketGroup,
        ticket_types: reorderedTypes
      };

      ticketGroupChanged(updatedTicketGroup);
    }
  }

  const moveIntoEmptyTicketGroup = (active, over, activeTicketType, activeTicketGroup, overTicketType, overTicketGroup) => {
    const removeTicketTypeFromGroups = (group) => {
      if (!group) return group;

      return {
        ...group,
        ticket_types: group.ticket_types.filter(tt => tt.id !== active.id),
        sub_groups: (group.sub_groups || []).map(subGroup =>
          removeTicketTypeFromGroups(subGroup)
        )
      };
    };
    let updatedRootGroup = removeTicketTypeFromGroups(rootTicketGroup);

    const updateEmptyTicketGroup = (group) => {
      if (group.id === over.data.current.ticketGroupId) {
        return {
          ...group,
          ticket_types: [
            ...group.ticket_types,
            {
              ...activeTicketType,
              position: group.ticket_types.length + 1
            }
          ]
        };
      }

      return {
        ...group,
        sub_groups: (group.sub_groups || []).map(subGroup =>
          updateEmptyTicketGroup(subGroup)
        )
      };
    };
    updatedRootGroup = updateEmptyTicketGroup(updatedRootGroup);

    ticketGroupChanged(updatedRootGroup);
  };

  const handleDragStart = ({ active }) => {
    setDragData({
      ticketType: active.data.current.ticketType,
      ticketGroup: active.data.current.ticketGroup
    });
  };

  const handleDragOver = ({ active, over }) => {
    if (!active) return;
    if (!over) return;
    if (active.id === over.id) return;

    const activeTicketType = active.data.current.ticketType;
    const activeTicketGroup = active.data.current.ticketGroup;

    const overTicketType = over.data.current?.ticketType;
    const overTicketGroup = over.data.current?.ticketGroup;

    const move = ticketDragMove(confirm, activeTicketType, activeTicketGroup, over.data.current);
    if (move === 'between') {
      moveBetweenTwoTicketGroups(active, over, activeTicketType, activeTicketGroup, overTicketType, overTicketGroup);
    } else if (move === 'within') {
      moveWithinTicketGroup(active, over, activeTicketType, activeTicketGroup, overTicketType, overTicketGroup);
    } else if (move === 'into') {
      moveIntoEmptyTicketGroup(active, over, activeTicketType, activeTicketGroup, overTicketType, overTicketGroup);
    }
  };

  const updateTicketTypePosition = (ticketType, raiseCapacity = false) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.patch("/teams/" + team.id + "/ticket_types/" + ticketType.id, {
        ticket_type: {
          position: ticketType.position,
          ticket_group_id: ticketType.ticket_group_id,
          ...(raiseCapacity && { raise_room_capacity: true })
        }
      })
      .then(({ data }) => {
        if (data.root_ticket_group) {
          ticketGroupChanged(data.root_ticket_group);
        }
      })
      .catch((error) => {
        const data = error.response?.data;
        reloadTicketTypes();
        if (data?.over_capacity !== undefined) {
          setRefusedDrag({ ticketType, overCapacity: data.over_capacity });
          return;
        }
        toast.error(Array.isArray(data) ? data.join(", ") : "Something went wrong moving this ticket. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const handleDragEnd = ({ active, over }) => {
    setDragData(null);

    const activeTicketType = active.data.current.ticketType;
    const activeTicketGroup = active.data.current.ticketGroup;

    const findContainingGroup = (group, ticketTypeId) => {
      if (group.ticket_types.some(tt => tt.id === ticketTypeId)) {
        return group;
      }

      for (const subGroup of (group.sub_groups || [])) {
        const foundGroup = findContainingGroup(subGroup, ticketTypeId);
        if (foundGroup) {
          return foundGroup;
        }
      }

      return null;
    };

    const containingGroup = findContainingGroup(rootTicketGroup, activeTicketType.id);
    const ticketTypeIndex = containingGroup?.ticket_types.findIndex(tt => tt.id === activeTicketType.id);

    if (ticketTypeIndex !== -1) {
      updateTicketTypePosition({
        ...activeTicketType,
        position: (ticketTypeIndex + 1),
        ticket_group_id: containingGroup.id
      });
    }
  };

  const [badgeColor, badgeLabel] = calendarClassificationBadge(confirm.calendar_classification);

  const menuItems = [
    { value: BASIC_INFO_MENU_ITEM, label: "Basic Info", icon: List },
    { value: DETAILS_MENU_ITEM, label: "Details", icon: Image01 },
    { value: TICKETS_MENU_ITEM, label: "Tickets", icon: Ticket01 },
    team.can_access_marketing_dashboard && { value: AUTOMATION_MENU_ITEM, label: "Automations", icon: Zap },
    { value: PROMOTION_MENU_ITEM, label: "Promotion", icon: Announcement01 },
    team.can_use_structured_checkout && { value: CUSTOMIZE_MENU_ITEM, label: "Customize", icon: Tool01 },
    { value: PUBLISH_MENU_ITEM, label: "Publish", icon: UploadCloud01 },
  ].filter(Boolean);

  const closeWarnBeforeLeaving = () => {
    warnBeforeLeavingOpenChanged(false);
    warnBeforeLeavingCallbackChanged(null);
  };

  const menuItemSelected = (menuItem) => {
    if(menuItem !== PUBLISH_MENU_ITEM) {
      checkWarnBeforeLeaving(() => {
        activeMenuItemChanged(menuItem);
      });
      return;
    }

    activeMenuItemChanged(PUBLISH_MENU_ITEM);

    if(!confirm.published_at) {
      warnBeforeLeavingChanged(true);

      window.addEventListener("beforeunload", beforeUnloadListener);

      var atags = [...document.querySelectorAll("a")]
        .filter(atag => !atag.onclick)
        .filter(atag => atag.getAttribute('beforeLeaveListener') !== "true")
        .filter(atag => atag.href !== "" && atag.href !== `${window.location}#`);

      atags.forEach(atag => {
        atag.setAttribute("beforeLeaveListener", "true");
        atag.addEventListener("click", (e) => {
          e.preventDefault();

          warnBeforeLeavingCallbackChanged(() => {
            window.location = atag.href;
          });

          warnBeforeLeavingOpenChanged(true);
        });
      });
    }
  };

  return (
    <DndContext sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}>
      <Modal
        open={warnBeforeLeavingOpen}
        onClose={closeWarnBeforeLeaving}
        type="error"
        title="Leave event unpublished?"
        description="Tickets will not go on sale until the event is published."
        confirmLabel="Confirm"
        onConfirm={() => {
          window.removeEventListener("beforeunload", beforeUnloadListener);

          if(warnBeforeLeavingCallback) {
            warnBeforeLeavingCallback();
          }

          warnBeforeLeavingChanged(false);
          closeWarnBeforeLeaving();
        }}
      >
        <p className="mb-0">Are you sure you want to leave without publishing the event?</p>
      </Modal>
      <Modal
        open={refusedDrag !== null}
        onClose={() => setRefusedDrag(null)}
        type="warning"
        title={refusedDrag ? `This would put the show over its capacity of ${refusedDrag.overCapacity}. Raise capacity too?` : ''}
        confirmLabel="Raise capacity"
        onConfirm={() => {
          if (!refusedDrag) return;
          const { ticketType } = refusedDrag;
          setRefusedDrag(null);
          updateTicketTypePosition(ticketType, true);
        }}
      />
      <ToastContainer icon={false} />
      <div className="row">
        <div className="col-12">
          <div className="page-title d-flex align-items-center">
            { confirm.name }
            <span className="ml-3">
              <Badge size="sm" color={badgeColor}>{badgeLabel}</Badge>
            </span>
          </div>
          <EventDetailsApp
            csrfToken={csrfToken}
            team={team}
            currentUser={currentUser}
            confirm={{...confirm}}
            confirmSaved={(savedConfirm) => {
              confirmChanged(savedConfirm);
            }}
            buyers={[...buyers]}
            promoters={promoters} />
        </div>
      </div>
      <div className="row mb-4">
        <div className="col-12">
          <Card padding="none"
                className={
                  activeMenuItem === CUSTOMIZE_MENU_ITEM && widgetConfig && widgetConfig.widget_type === 'custom'
                    ? ''
                    : 'overflow-hidden'
                }>
            <div className="row mx-0">
              <div className="col-12 col-md-auto"
                   style={{
                     "padding": "22px 20px",
                     "minWidth": "230px",
                     "background": "var(--sl-bg-secondary)"
                   }}>
                <h3 style={{"fontSize": "24px", "marginBottom": "28px"}}>
                  <strong>
                    Ticketing
                  </strong>
                </h3>
                <Tabs orientation="vertical"
                      variant="button-border"
                      track={false}
                      aria-label="Ticketing"
                      items={menuItems}
                      value={activeMenuItem}
                      onChange={menuItemSelected}
                      collapseBelow={0} />
                <hr style={{
                      "borderTop": "1px solid var(--sl-border-secondary)",
                      "marginTop": "20px",
                      "marginBottom": "12px"
                    }} />
                <Button variant="tertiary"
                        size="sm"
                        className="w-100 justify-content-between"
                        trailingIcon={ChevronRight}
                        onClick={() => {
                          checkWarnBeforeLeaving(() => {
                            statementDescriptorSuffixChanged(confirm.statement_descriptor_suffix);
                            activeMenuItemChanged(SETTINGS_MENU_ITEM);
                          });
                        }}>
                  Settings
                </Button>
              </div>
              <div className="col-12 col-md ticketing-content"
                   style={
                     activeMenuItem === CUSTOMIZE_MENU_ITEM && widgetConfig && (widgetConfig.widget_type === 'custom' || widgetConfig.widget_type === 'festival')
                       ? { padding: 0 }
                       : { paddingTop: '38px', paddingBottom: '38px' }
                   }>
                {activeMenuItem === BASIC_INFO_MENU_ITEM ? (
                  <BasicInfo csrfToken={csrfToken}
                             team={team}
                             currentUser={currentUser}
                             activeMenuItemChanged={activeMenuItemChanged}
                             updateConfirm={updateConfirm}
                             autoSaveConfirm={autoSaveConfirm}
                             venuesOptions={venuesOptions}
                             rooms={rooms}
                             venueOwnerships={venueOwnerships}
                             performers={performers}
                             deletePerformer={deletePerformer}
                             createPerformer={createPerformer}
                             updatePerformer={updatePerformer}
                             performerDragged={performerDragged}
                             admissionSeatingChartEvent={admissionSeatingChartEvent}
                             addOnSeatingChartEvent={addOnSeatingChartEvent}
                             confirm={confirm} />
                ) : activeMenuItem === DETAILS_MENU_ITEM ? (
                  <Details csrfToken={csrfToken}
                           team={team}
                           currentUser={currentUser}
                           activeMenuItemChanged={activeMenuItemChanged}
                           pageDetails={pageDetails}
                           createPageDetail={createPageDetail}
                           deletePageDetail={deletePageDetail}
                           pageDetailChanged={pageDetailChanged}
                           autoSavePageDetail={autoSavePageDetail}
                           pageDetailDragged={pageDetailDragged}
                           imageCalendarEventAttachments={imageCalendarEventAttachments}
                           uploadPageDetailImage={uploadPageDetailImage}
                           uploadPageDetailImageUrl={uploadPageDetailImageUrl}
                           uploadConfirmMainImageFile={uploadConfirmMainImageFile}
                           uploadConfirmMainImageUrl={uploadConfirmMainImageUrl}
                           uploadConfirmWebsiteImageFile={uploadConfirmWebsiteImageFile}
                           uploadConfirmWebsiteImageUrl={uploadConfirmWebsiteImageUrl}
                           mainEventImageLoading={mainEventImageLoading}
                           websiteEventImageLoading={websiteEventImageLoading}
                           rootUrl={rootUrl}
                           artistBios={artistBios}
                           artistBiosChanged={artistBiosChanged}
                           pageDetailTemplates={pageDetailTemplates}
                           pageDetailTemplate={pageDetailTemplate}
                           pageDetailTemplateChanged={pageDetailTemplateChanged}
                           createPageDetailTemplate={createPageDetailTemplate}
                           deletePageDetailTemplate={deletePageDetailTemplate}
                           confirm={confirm} />
                ) : activeMenuItem === TICKETS_MENU_ITEM ? (
                  <Tickets csrfToken={csrfToken}
                           team={team}
                           currentUser={currentUser}
                           activeMenuItemChanged={activeMenuItemChanged}
                           activeTicketingMenuItem={activeTicketingMenuItem}
                           activeTicketingMenuItemChanged={activeTicketingMenuItemChanged}
                           ticketTypeForForm={ticketTypeForForm}
                           ticketTypeForFormChanged={ticketTypeForFormChanged}
                           fetchNewTicketType={fetchNewTicketType}
                           rootTicketGroup={rootTicketGroup}
                           ticketGroupChanged={ticketGroupChanged}
                           ticketTypesChanged={ticketTypesChanged}
                           isLoadingDefaultTicketTypesChanged={isLoadingDefaultTicketTypesChanged}
                           ticketTypes={ticketTypes}
                           fetchTicketTypeForEdit={fetchTicketTypeForEdit}
                           addOns={addOns}
                           addOnForForm={addOnForForm}
                           fetchNewAddOn={fetchNewAddOn}
                           addOnForFormChanged={addOnForFormChanged}
                           createAddOn={createAddOn}
                           fetchAddOnToEdit={fetchAddOnToEdit}
                           deleteAddOn={deleteAddOn}
                           updateAddOn={updateAddOn}
                           addOnDragged={addOnDragged}
                           exampleTicket={exampleTicket}
                           buildExampleTicket={buildExampleTicket}
                           exampleTicketChanged={exampleTicketChanged}
                           seatingCharts={seatingCharts}
                           seatsIOPublicKey={seatsIOPublicKey}
                           updateConfirm={updateConfirm}
                           autoSaveConfirm={autoSaveConfirm}
                           isLoadingDefaultTicketTypes={isLoadingDefaultTicketTypes}
                           addOnSeatingCharts={addOnSeatingCharts}
                           isEditingAddOnsIntro={isEditingAddOnsIntro}
                           isEditingAddOnsIntroChanged={isEditingAddOnsIntroChanged}
                           addOnsIntroEditorState={addOnsIntroEditorState}
                           addOnsIntroEditorStateChanged={addOnsIntroEditorStateChanged}
                           isLoadingDefaultAddOns={isLoadingDefaultAddOns}
                           isEditingAddOnsAlertMessage={isEditingAddOnsAlertMessage}
                           isEditingAddOnsAlertMessageChanged={isEditingAddOnsAlertMessageChanged}
                           addOnsAlertMessageEditorState={addOnsAlertMessageEditorState}
                           addOnsAlertMessageEditorStateChanged={addOnsAlertMessageEditorStateChanged}
                           confirm={confirm}
                           confirmChanged={confirmChanged}
                           manualTaxes={manualTaxes}
                           addOnAdded={addOnAdded}
                           feeRuleSets={feeRuleSets}
                           showActivities={showActivities}
                           seatingChartEvents={seatingChartEvents}
                           ticketTypeIsSeated={ticketTypeIsSeated}
                           preSaleEnabled={preSaleEnabled}
                           reloadTicketTypes={reloadTicketTypes}
                            reloadAddOns={reloadAddOns}
                           isLoadingDefaultAddOnsChanged={isLoadingDefaultAddOnsChanged}
                           admissionSeatingChartEvent={admissionSeatingChartEvent}
                           admissionSeatingChartEventChanged={admissionSeatingChartEventChanged}
                           addOnSeatingChartEvent={addOnSeatingChartEvent}
                            addOnSeatingChartEventChanged={addOnSeatingChartEventChanged}
                            seatingChartEventsChanged={seatingChartEventsChanged}
                            holds={holds}
                            fetchHolds={fetchHolds}
                            createHold={createHold}
                            updateHold={updateHold}
                            deleteHold={deleteHold}
                            generalAdmissionHolds={generalAdmissionHolds}
                            fetchGeneralAdmissionHolds={fetchGeneralAdmissionHolds}
                            createGeneralAdmissionHold={createGeneralAdmissionHold}
                            updateGeneralAdmissionHold={updateGeneralAdmissionHold}
                            deleteGeneralAdmissionHold={deleteGeneralAdmissionHold}
                            kills={kills}
                            fetchKills={fetchKills}
                            createKill={createKill}
                            updateKill={updateKill}
                            deleteKill={deleteKill}
                            addOnsIntroTemplates={addOnsIntroTemplates}
                            addOnsAlertMessageTemplates={addOnsAlertMessageTemplates}
                            addOnsIntroTemplateDraft={addOnsIntroTemplateDraft}
                            addOnsAlertMessageTemplateDraft={addOnsAlertMessageTemplateDraft}
                            addOnMessageTemplateChanged={addOnMessageTemplateChanged}
                            createAddOnMessageTemplate={createAddOnMessageTemplate}
                            deleteAddOnMessageTemplate={deleteAddOnMessageTemplate}
                            />
                ) : activeMenuItem === AUTOMATION_MENU_ITEM ? (
                  <Automations csrfToken={csrfToken}
                               team={team}
                               confirm={confirm}
                               activeMenuItemChanged={activeMenuItemChanged} />
                ) : activeMenuItem === PROMOTION_MENU_ITEM ? (
                  <Promotions csrfToken={csrfToken}
                              team={team}
                              confirm={confirm}
                              confirmChanged={confirmChanged}
                              autoSaveConfirm={autoSaveConfirm}
                              performers={performers}
                              activeMenuItemChanged={activeMenuItemChanged} />
                ) : activeMenuItem === CUSTOMIZE_MENU_ITEM ? (
                  <Customize csrfToken={csrfToken}
                              team={team}
                              confirm={confirm}
                              confirmChanged={confirmChanged}
                              autoSaveConfirm={autoSaveConfirm}
                              checkoutCategories={checkoutCategories}
                              checkoutCategoriesChanged={checkoutCategoriesChanged}
                              ticketTypes={ticketTypes}
                              addOns={addOns}
                              seatingChartEvents={seatingChartEvents}
                              membershipProducts={membershipProducts}
                              widgetConfig={widgetConfig}
                              widgetConfigChanged={widgetConfigChanged}
                              webOrderFormProps={webOrderFormProps}
                              widgetPickerAvailable={widgetPickerAvailable}
                              eventOrderFormAvailable={eventOrderFormAvailable}
                              customWidgetAvailable={customWidgetAvailable}
                              checkoutStructureSelfManaged={checkoutStructureSelfManaged}
                              markCheckoutStructureSelfManaged={markCheckoutStructureSelfManaged}
                              reloadCheckoutCategories={reloadCheckoutCategories}
                              reloadTicketTypes={reloadTicketTypes}
                              reloadAddOns={reloadAddOns}
                              activeMenuItemChanged={activeMenuItemChanged} />
                ) : activeMenuItem === PUBLISH_MENU_ITEM ? (
                  <Publish csrfToken={csrfToken}
                           team={team}
                           currentUser={currentUser}
                           ticketTypes={ticketTypes}
                           pageDetails={pageDetails}
                           updateConfirm={updateConfirm}
                           publishConfirm={publishConfirm}
                           onStepSelected={menuItemSelected}
                           rootUrl={rootUrl}
                           confirm={confirm} />
                ) : activeMenuItem === SETTINGS_MENU_ITEM ? (
                  <Settings csrfToken={csrfToken}
                            team={team}
                            currentUser={currentUser}
                            updateConfirm={updateConfirm}
                            statementDescriptorSuffix={statementDescriptorSuffix}
                            statementDescriptorSuffixChanged={statementDescriptorSuffixChanged}
                            maxDescriptorLength={maxDescriptorLength}
                            updateStatementDescriptorSuffix={updateStatementDescriptorSuffix}
                            requirePerTicketCustomChargeTypes={requirePerTicketCustomChargeTypes}
                            createRequirePerTicketCustomChargeType={createRequirePerTicketCustomChargeType}
                            requirePerTicketCustomChargeTypeChanged={requirePerTicketCustomChargeTypeChanged}
                            deleteRequirePerTicketCustomChargeType={deleteRequirePerTicketCustomChargeType}
                            autoSaveRequirePerTicketCustomChargeType={autoSaveRequirePerTicketCustomChargeType}
                            googleMarketingPixels={googleMarketingPixels}
                            setGoogleMarketingPixels={setGoogleMarketingPixels}
                            facebookMarketingPixels={facebookMarketingPixels}
                            setFacebookMarketingPixels={setFacebookMarketingPixels}
                            tiktokMarketingPixels={tiktokMarketingPixels}
                            setTiktokMarketingPixels={setTiktokMarketingPixels}
                            spotifyMarketingPixels={spotifyMarketingPixels}
                            setSpotifyMarketingPixels={setSpotifyMarketingPixels}
                            xMarketingPixels={xMarketingPixels}
                            setXMarketingPixels={setXMarketingPixels}
                            confirmationPageTemplates={confirmationPageTemplates}
                            confirm={confirm} />
                ) : null}
              </div>
            </div>
          </Card>
        </div>
      </div>
      <DragOverlay>
        {dragData ? (
          <div className="table-responsive">
          <table
            className="table m-0"
            style={{
              color: "var(--sl-text-tertiary)",
              border: "none",
              fontSize: "14px",
              background: "var(--sl-bg-primary)"
            }}
          >
            <colgroup>
              <col span={1} style={{width: "3%", minWidth: "20px"}} />
              <col span={1} style={{width: "45%", minWidth: "350px"}} />
              <col span={1} style={{width: "30%", minWidth: "240px"}} />
              <col span={1} style={{width: "10%", minWidth: "60px"}} />
              <col span={1} style={{width: "10%", minWdith: "100px"}} />
              <col span={1} style={{width: "2%", minWidth: "20px"}} />
            </colgroup>
            <tbody>
              <TicketTypeRow csrfToken={csrfToken}
                              team={team}
                              currentUser={currentUser}
                              confirm={confirm}
                              fetchNewTicketType={fetchNewTicketType}
                              ticketGroup={dragData.ticketGroup}
                              ticketType={dragData.ticketType}
                              ticketTypeIsSeated={ticketTypeIsSeated}
                              canAddSubGroup={false}
                              isLast={false}
                             ticketGroupChanged={ticketGroupChanged}
                             ticketTypesChanged={ticketTypesChanged}
                             fetchTicketTypeForEdit={fetchTicketTypeForEdit} />
            </tbody>
          </table>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

ConfirmTicketing.propTypes = {
  seatingChartEventsChanged: PropTypes.func,
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  confirmChanged: PropTypes.func.isRequired,
  activeMenuItem: PropTypes.string,
  activeMenuItemChanged: PropTypes.func.isRequired,
  updateConfirm: PropTypes.func.isRequired,
  autoSaveConfirm: PropTypes.func.isRequired,
  venuesOptions: PropTypes.array,
  rooms: PropTypes.array,
  venueOwnerships: PropTypes.array,
  performers: PropTypes.array,
  deletePerformer: PropTypes.func.isRequired,
  createPerformer: PropTypes.func.isRequired,
  updatePerformer: PropTypes.func.isRequired,
  pageDetails: PropTypes.array,
  createPageDetail: PropTypes.func.isRequired,
  deletePageDetail: PropTypes.func.isRequired,
  pageDetailChanged: PropTypes.func.isRequired,
  autoSavePageDetail: PropTypes.func.isRequired,
  pageDetailDragged: PropTypes.func.isRequired,
  imageCalendarEventAttachments: PropTypes.array,
  uploadPageDetailImage: PropTypes.func.isRequired,
  uploadPageDetailImageUrl: PropTypes.func.isRequired,
  uploadConfirmMainImageFile: PropTypes.func.isRequired,
  uploadConfirmMainImageUrl: PropTypes.func.isRequired,
  activeTicketingMenuItem: PropTypes.string,
  activeTicketingMenuItemChanged: PropTypes.func.isRequired,
  ticketTypeForForm: PropTypes.object,
  ticketTypeForFormChanged: PropTypes.func.isRequired,
  fetchNewTicketType: PropTypes.func.isRequired,
  fetchTicketTypeForEdit: PropTypes.func.isRequired,
  mainEventImageLoading: PropTypes.bool,
  publishConfirm: PropTypes.func.isRequired,
  rootUrl: PropTypes.string.isRequired,
  addOns: PropTypes.array,
  addOnForForm: PropTypes.object,
  fetchNewAddOn: PropTypes.func.isRequired,
  addOnForFormChanged: PropTypes.func.isRequired,
  createAddOn: PropTypes.func.isRequired,
  fetchAddOnToEdit: PropTypes.func.isRequired,
  deleteAddOn: PropTypes.func.isRequired,
  updateAddOn: PropTypes.func.isRequired,
  addOnDragged: PropTypes.func.isRequired,
  statementDescriptorSuffix: PropTypes.string,
  statementDescriptorSuffixChanged: PropTypes.func.isRequired,
  maxDescriptorLength: PropTypes.number,
  updateStatementDescriptorSuffix: PropTypes.func.isRequired,
  artistBios: PropTypes.object,
  artistBiosChanged: PropTypes.func.isRequired,
  pageDetailTemplates: PropTypes.array,
  deletePageDetailTemplate: PropTypes.func.isRequired,
  pageDetailTemplate: PropTypes.object,
  pageDetailTemplateChanged: PropTypes.func.isRequired,
  createPageDetailTemplate: PropTypes.func.isRequired,
  confirmationPageTemplates: PropTypes.array,
  performerDragged: PropTypes.func.isRequired,
  exampleTicket: PropTypes.object,
  buildExampleTicket: PropTypes.func.isRequired,
  exampleTicketChanged: PropTypes.func.isRequired,
  seatingCharts: PropTypes.array,
  isLoadingDefaultTicketTypes: PropTypes.bool,
  addOnSeatingCharts: PropTypes.array,
  isEditingAddOnsIntro: PropTypes.bool,
  isEditingAddOnsIntroChanged: PropTypes.func.isRequired,
  addOnsIntroEditorState: PropTypes.object,
  addOnsIntroEditorStateChanged: PropTypes.func.isRequired,
  isLoadingDefaultAddOns: PropTypes.bool,
  isEditingAddOnsAlertMessage: PropTypes.bool,
  isEditingAddOnsAlertMessageChanged: PropTypes.func.isRequired,
  addOnsAlertMessageEditorState: PropTypes.object,
  addOnsAlertMessageEditorStateChanged: PropTypes.func.isRequired,
  buyers: PropTypes.array,
  promoters: PropTypes.array,
  requirePerTicketCustomChargeTypes: PropTypes.array,
  createRequirePerTicketCustomChargeType: PropTypes.func.isRequired,
  requirePerTicketCustomChargeTypeChanged: PropTypes.func.isRequired,
  deleteRequirePerTicketCustomChargeType: PropTypes.func.isRequired,
  autoSaveRequirePerTicketCustomChargeType: PropTypes.func.isRequired,
  showActivities: PropTypes.array.isRequired,
  manualTaxes: PropTypes.array,
  warnBeforeLeaving: PropTypes.bool.isRequired,
  warnBeforeLeavingChanged: PropTypes.func.isRequired,
  warnBeforeLeavingOpen: PropTypes.bool.isRequired,
  warnBeforeLeavingOpenChanged: PropTypes.func.isRequired,
  warnBeforeLeavingCallback: PropTypes.func,
  warnBeforeLeavingCallbackChanged: PropTypes.func.isRequired,
  addOnAdded: PropTypes.func.isRequired,
  feeRuleSets: PropTypes.array,
  checkoutCategories: PropTypes.array,
  checkoutCategoriesChanged: PropTypes.func.isRequired,
  seatingChartEvents: PropTypes.array,
  membershipProducts: PropTypes.array,
  widgetConfig: PropTypes.object,
  widgetConfigChanged: PropTypes.func.isRequired,
  widgetPickerAvailable: PropTypes.bool,
  eventOrderFormAvailable: PropTypes.bool,
  customWidgetAvailable: PropTypes.bool,
  checkoutStructureSelfManaged: PropTypes.bool,
  markCheckoutStructureSelfManaged: PropTypes.func.isRequired,
  rootTicketGroup: PropTypes.object.isRequired,
  ticketGroupChanged: PropTypes.func.isRequired,
  isLoadingDefaultTicketTypesChanged: PropTypes.func.isRequired,
  seatsIOPublicKey: PropTypes.string,
  addOnsChanged: PropTypes.func.isRequired,
  isLoadingDefaultAddOnsChanged: PropTypes.func.isRequired,
  kills: PropTypes.array,
  fetchKills: PropTypes.func.isRequired,
  createKill: PropTypes.func.isRequired,
  updateKill: PropTypes.func.isRequired,
  deleteKill: PropTypes.func.isRequired,
  holds: PropTypes.array,
  fetchHolds: PropTypes.func,
  createHold: PropTypes.func,
  updateHold: PropTypes.func,
  deleteHold: PropTypes.func,
  generalAdmissionHolds: PropTypes.array,
  fetchGeneralAdmissionHolds: PropTypes.func,
  createGeneralAdmissionHold: PropTypes.func,
  updateGeneralAdmissionHold: PropTypes.func,
  deleteGeneralAdmissionHold: PropTypes.func,
  addOnsIntroTemplates: PropTypes.array,
  addOnsAlertMessageTemplates: PropTypes.array,
  addOnsIntroTemplateDraft: PropTypes.object,
  addOnsAlertMessageTemplateDraft: PropTypes.object,
  addOnMessageTemplateChanged: PropTypes.func.isRequired,
  createAddOnMessageTemplate: PropTypes.func.isRequired,
  deleteAddOnMessageTemplate: PropTypes.func.isRequired
};

export default ConfirmTicketing;
